import React from 'react'
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ButtonGroup,
  Collapse,
  FormGroup,
  Label,
  Media
} from 'reactstrap'
import {
  PackTemplatesApi,
  PrefsApi,
  DiscountType,
  UsersApi
} from 'typescript-axios'
import Link from 'next/link'
import Slider from '@mui/material/Slider'
import ReactHtmlParser from 'react-html-parser'
import axios from 'axios'
import { getCurrencySign, getCurrencyStr } from 'common'
import { withSession } from '../utils/session'
import withAuth from '../hocs/WithAuth'
import withLocale from '../hocs/WithLocale'
import { translate } from '../translations/utils'
import useTranslation from '../hooks/UseTranslation'
import { UserContext } from '../contexts/UserContext'
import Packs from '../components/Packs'
import { getAxiosParams } from '../utils/client'

import HeroAndButton from '../components/HeroAndButton'
import GuruReferral from '../components/GuruReferral'
import PromoCodes from '../components/PromoCodes'
import ContainerCol from '../components/ContainerCol'
import Integration from '../components/Integration'
import { toastPromise } from '../utils/toast'

const PREFS_API = new PrefsApi(getAxiosParams())
const USER_API = new UsersApi(getAxiosParams())
const PACK_TEMPLATES_SERVICE = new PackTemplatesApi(getAxiosParams())

const readPacks = (options) =>
  PACK_TEMPLATES_SERVICE.readPackTemplatesApiV1PackTemplatesGet(
    undefined,
    undefined,
    true,
    undefined,
    undefined,
    options
  )

export const getServerSideProps = withSession(
  async ({ locale, defaultLocale, options }) => {
    const templatesReq = readPacks(options)
    // const listingTemplatesReq = LISTING_TEMPLATES_API.readListingTemplatesApiV1TemplatesGet(
    //     undefined,
    //     undefined,
    //     undefined,
    //     undefined,
    //     options
    // )
    const prefsReq = PREFS_API.readPrefsApiV1PrefsGet(options)

    await axios.all([templatesReq, prefsReq])

    const templates = await templatesReq.then((res) => res.data)
    // const listingTemplates = await listingTemplatesReq.then(res => res.data)
    const prefs = await prefsReq.then((res) => res.data)

    return {
      props: {
        nav: {
          light: true,
          classes: 'shadow',
          color: 'white'
        },
        data: { templates, prefs, listingTemplates: null },
        loggedUser: true,
        title: translate('packs_and_subscriptions', locale, defaultLocale),
        locale,
        defaultLocale
      }
    }
  }
)

export const formatDiscount = (amount, type, currency) => {
  if (type === DiscountType.NUMBER_0) {
    return `${amount}%`
  }
  return getCurrencyStr(amount / 100.0, currency)
}

export function getFirstClassDiscountString(amount, type, currency, t) {
  if (type === DiscountType.NUMBER_0 && amount === 100) {
    return ReactHtmlParser(t('first_class_discount_free'))
  }
  const str = formatDiscount(amount, type, currency)
  return ReactHtmlParser(t('first_class_discount_str', str))
}

export function getFirstClassDiscountBadge(amount, type, currency, t) {
  return (
    amount != null &&
    amount > 0 && (
      <h5>
        <Badge color='premium'>
          {getFirstClassDiscountString(amount, type, currency, t)}
        </Badge>
      </h5>
    )
  )
}

const Page = (props) => {
  const { t } = useTranslation()
  const { setLoggedUser } = React.useContext(UserContext)
  const [packs, setPacks] = React.useState(props.data.templates)
  const [prefs, setPrefs] = React.useState(props.data.prefs)
  const [discountDisabled, setDiscountDisabled] = React.useState(true)

  const submitUpdate = async (p) =>
    toastPromise(
      PREFS_API.updatePrefsApiV1PrefsPut(p).then((res) => setPrefs(res.data)),
      t
    ).then(() =>
      USER_API.readUserMeApiV1UsersMeGet().then((r) => setLoggedUser(r.data))
    )

  React.useEffect(() => {
    setLoggedUser(props.data.user)
  }, [])

  const isDiscountPct =
    prefs.first_class_discount_type === DiscountType.NUMBER_0

  const onCreateCallback = (newPack) =>
    setPacks(packs ? [...packs, newPack] : [newPack])
  const onDeleteCallback = () => readPacks().then((res) => setPacks(res.data))
  const toggleDiscountType = (newType) =>
    setPrefs((p) => ({
      ...p,
      first_class_discount_type: newType,
      first_class_discount: 0
    }))

  return (
    <section className='py-5'>
      <ContainerCol>
        <Breadcrumb listClassName='pl-0  justify-content-start'>
          <BreadcrumbItem className='text-first-letter'>
            <Link href='/guru'>
              <a>{t('guru_page_title')}</a>
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem className='text-first-letter' active>
            {t('packs_and_subscriptions')}
          </BreadcrumbItem>
        </Breadcrumb>
        <HeroAndButton
          hero={t('packs_and_subscriptions')}
          btn={{
            onClick: () => null,
            label: ''
          }}
        />
        <div className='text-block' id='classes' key='classes'>
          <h5 className='text-first-letter mb-4'>{t('prefs_classes')}</h5>
          <Media className='text-muted mb-4'>
            <Media body className='mt-n1'>
              <div
                dangerouslySetInnerHTML={{ __html: t('prefs_classes_content') }}
              />
            </Media>
          </Media>
          <div className='text-muted'>
            <div className='mb-2'>
              {(prefs.first_class_discount == null ||
                prefs.first_class_discount === 0) &&
                t('prefs_first_class_discount_0')}
              {prefs.first_class_discount !== null &&
                prefs.first_class_discount > 0 &&
                ReactHtmlParser(
                  t(
                    'prefs_first_class_discount_val',
                    formatDiscount(
                      prefs.first_class_discount,
                      prefs.first_class_discount_type,
                      'EUR'
                    )
                  )
                )}
              {isDiscountPct &&
                prefs.first_class_discount === 100 &&
                ReactHtmlParser(t('prefs_first_class_discount_100'))}
            </div>
            <div className='text-right'>
              <Button
                color='link'
                className='mb-3 p-0'
                onClick={() => setDiscountDisabled(!discountDisabled)}
              >
                {t(discountDisabled ? 'update' : 'save')}
              </Button>
            </div>
            <Collapse
              isOpen={!discountDisabled}
              onExit={() => submitUpdate(prefs)}
              className='px-4'
            >
              <div>
                <FormGroup>
                  <Label className='form-label'>
                    {t('prefs_first_class_discount_type')}
                  </Label>
                  <br />
                  <ButtonGroup>
                    <Button
                      color={
                        prefs.first_class_discount_type ===
                        DiscountType.NUMBER_0 // FIXME DiscountType.PCT
                          ? 'primary'
                          : 'outline-primary'
                      }
                      onClick={() => toggleDiscountType(DiscountType.NUMBER_0)}
                    >
                      %
                    </Button>
                    <Button
                      color={
                        prefs.first_class_discount_type ===
                        DiscountType.NUMBER_1 // FIXME DiscountType.CCY
                          ? 'primary'
                          : 'outline-primary'
                      }
                      onClick={() => toggleDiscountType(DiscountType.NUMBER_1)}
                    >
                      {getCurrencySign('EUR')}
                    </Button>
                  </ButtonGroup>
                </FormGroup>
                <FormGroup>
                  <Label className='form-label'>
                    {t('prefs_first_class_discount_amount')}
                  </Label>
                  <Slider
                    color='primary'
                    disabled={discountDisabled}
                    aria-label='First class discount'
                    // getAriaValueText={valuetext}
                    valueLabelDisplay='off'
                    min={0}
                    max={isDiscountPct ? 100 : 25}
                    step={isDiscountPct ? 5 : 0.5}
                    onChange={(e, newVal) =>
                      setPrefs(
                        {
                          ...prefs,
                          first_class_discount: isDiscountPct
                            ? newVal
                            : newVal * 100
                        },
                        false
                      )
                    }
                    value={
                      prefs.first_class_discount /
                        (isDiscountPct ? 1 : 100.0) || 0
                    }
                  />
                </FormGroup>
                <Media className='text-muted mb-4'>
                  <Media body className='mt-n1'>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: t('prefs_classes_disclaimer')
                      }}
                    />
                  </Media>
                </Media>
              </div>
            </Collapse>
          </div>
        </div>
        <Packs
          packs={packs}
          onCreateCallback={onCreateCallback}
          onDeleteCallback={onDeleteCallback}
        />

        <div
          className='text-block scroll-margin-top'
          id='promo-codes'
          key='promo-codes'
        >
          <PromoCodes listingTemplates={props.data.listingTemplates} />
        </div>

        <div
          className='text-block scroll-margin-top'
          id='referral'
          key='referral'
        >
          <GuruReferral />
        </div>

        <div
          className='text-block scroll-margin-top'
          id='integration'
          key='integration'
        >
          <Integration />
        </div>
      </ContainerCol>
    </section>
  )
}

export default withAuth(withLocale(Page), true)
